import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  ModalSubmitButton,
  SectionWithBorderField,
  showModal,
} from '@edna/components';
import {FieldError} from '@edna/components/FormField';
import {Stack} from '@edna/components/primitives';
import {useOnChange} from '@edna/hooks';
import {ECurrency, formatNumber} from '@edna/utils';
import {ECurrencySymbol} from '@edna/utils/formatMoney';

import {SecondaryText, Text} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {PayKeeperLogoIcon} from 'src/icons';
import {joiResolver} from 'src/utils/validator';

import {
  useCreatePayKeeperAutopaymentOrderMutation,
  useGetPayKeeperAutopaymentInfoQuery,
} from '../../api';
import {MIN_PAYKEEPER_AMOUNT, VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT} from '../../definitions';
import {sendForm} from '../../sendPayKeeperForm';
import * as S from '../../style';
import {useAutopaymentRecommendedThreshold} from '../useAutopaymentRecommendedThreshold';

type TModalProps = {
  isConnect?: boolean;
  closeModal: TEmptyFunction;
};

const PayKeeperSettingsModal = React.memo<TModalProps>(({closeModal, isConnect}) => {
  const {t} = useTranslation();

  const {data: info} = useGetPayKeeperAutopaymentInfoQuery();
  const [createPayKeeperOrder, {isLoading}] = useCreatePayKeeperAutopaymentOrderMutation();

  const recommendedThreshold = useAutopaymentRecommendedThreshold();

  const defaultValues = React.useMemo(
    () => ({sum: info?.recurrentPaymentSum || recommendedThreshold || null}),
    [info, recommendedThreshold],
  );

  const methods = useForm<{sum: number | null}>({
    mode: 'all',
    defaultValues,
    resolver: joiResolver((joi) =>
      joi.object({
        sum: joi.number().required().min(MIN_PAYKEEPER_AMOUNT),
      }),
    ),
  });

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  const sumError = methods.formState.errors.sum;
  const sum = methods.watch('sum');

  const handleSave = React.useCallback(
    async (formValues: {sum: number | null}) => {
      try {
        const orderInfo = await createPayKeeperOrder({
          recurrentPaymentSum: formValues.sum ?? 0,
          sum: VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT,
        }).unwrap();

        sendForm(`${orderInfo.paykeeperServerUrl}/create/`, {
          orderid: orderInfo.orderId,
          clientid: orderInfo.clientId,
          client_email: orderInfo.clientEmail,
          service_name: orderInfo.serviceName,
          sum: VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT,
          msgtype: 'createbinding',
        });

        closeModal();
      } catch (e) {}
    },
    [closeModal],
  );

  return (
    <Modal width="530px">
      <FormProvider {...methods}>
        <ModalHeader>
          {t(
            `BalanceReplenishment:payKeeperAutopaymentSettingsModal.${isConnect ? 'title' : 'editTitle'}`,
          )}
        </ModalHeader>
        <ModalContent>
          <S.Wrapper>
            {!!recommendedThreshold && (
              <SecondaryText size="S">
                <Trans
                  i18nKey="BalanceReplenishment:payKeeperAutopaymentSettingsModal.licensesInfo"
                  values={{threshold: formatNumber(recommendedThreshold)}}
                >
                  <Text size="S" weight={500} isInline />
                </Trans>
              </SecondaryText>
            )}
            <Stack isFullWidth justify="center">
              <PayKeeperLogoIcon />
            </Stack>
            <SectionWithBorderField
              name="sum"
              htmlFor="sum"
              size="1"
              label={t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.amount')}
              hint={t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.minAmount')}
            >
              <Stack size="2" direction="ROW" align="center">
                <S.PayKeeperNumberInputField name="sum" showError={false} noMargin />
                <Text size="S">{ECurrencySymbol[ECurrency.RUB]}</Text>
              </Stack>
              {!!sumError && (
                <FieldError>
                  {t(sumError?.message ?? '', sumError?.ref?.value) as string}
                </FieldError>
              )}
            </SectionWithBorderField>
          </S.Wrapper>
          {!!sum && Number(sum) < recommendedThreshold && (
            <Banner type="WARNING" fontSize="S" shadowSize="XS">
              {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.lowSumWarning')}
            </Banner>
          )}
          <SecondaryText size="S">
            {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.validationInfo')}
            {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.acceptanceInfo')}
          </SecondaryText>
        </ModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton
            onClick={methods.handleSubmit(handleSave)}
            disabled={!methods.formState.isValid || isLoading}
          >
            {t('Common:button.continue')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

PayKeeperSettingsModal.displayName = 'PayKeeperSettingsModal';

const showPayKeeperSettingsModal = (isConnect?: boolean) =>
  showModal((closeModal) => (
    <PayKeeperSettingsModal isConnect={isConnect} closeModal={closeModal} />
  ));

export {showPayKeeperSettingsModal};
